<template>
  <SideSheet
    class="terms-signature"
    data-testid="terms-signature"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
    </NavBar>
    <section class="terms-signature__content">
      <HeadingDisplay
        size="small"
        class="terms-signature__header"
        title="Para o investimento ser solicitado, é necessário o aceite dos termos"
        subtitle="Ao continuar, você declara que leu e está de acordo com todos os termos estabelecidos."
      />
      <ContentDisplay content-aspect="outside" class="terms-signature__details">
        <template #content-block-1>
          <div class="terms-signature__heading">
            <div>
              <BaseText
                v-if="productDescription"
                :color="NebraskaColors.elementSecondary"
                size="p5"
              >
                {{ productDescription }}
              </BaseText>
              <Heading :level="6">
                {{ productName }}
              </Heading>
            </div>
            <InstrumentAvatar
              v-if="productSymbol"
              size="small"
              :symbol="productSymbol || ''"
              :alt="productSymbol || ''"
            />
          </div>
          <Row
            v-for="(item, index) in documents"
            :key="index"
            :has-line="index !== 0"
            :left-title="item.label"
            has-interaction
            @click="openDocument(item.url)"
          >
            <template #icon-right>
              <BaseIcon icon="EA0080" />
            </template>
          </Row>
        </template>
      </ContentDisplay>
    </section>
    <ButtonContainer class="terms-signature__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          @click="$emit('confirm')"
        />
      </template>
    </ButtonContainer>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';

import {
  NavBar,
  HeadingDisplay,
  ContentDisplay,
  Heading,
  Row,
  BaseIcon,
  ButtonContainer,
  ButtonPrimary,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';

import { removeBodyScroll } from '@/modules/common/helpers/browser';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import InstrumentAvatar from '@/modules/trade/components/InstrumentAvatar/InstrumentAvatar.vue';

interface IData {
  requestTermsSignature: {
    productId: string;
    productName: string;
    productDescription?: string;
    symbol?: string;
    documents: {
      label: string;
      url: string;
    }[];
  }[];
}

interface EmitEvents {
  (event: 'close'): void;
  (event: 'confirm'): void;
}
defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
  data: IData;
}
const props = defineProps<Props>();

const productName = computed(
  () => props.data.requestTermsSignature[0].productName
);

const productDescription = computed(
  () => props.data.requestTermsSignature[0].productDescription
);

const productSymbol = computed(
  () => props.data.requestTermsSignature[0].symbol
);

const documents = computed(() => props.data.requestTermsSignature[0].documents);

function openDocument(url: string) {
  window.open(url, '_blank');
}

watch(
  () => props.isOpen,
  _isOpen => {
    removeBodyScroll();
  }
);
</script>

<style lang="less" scoped>
.terms-signature {
  height: 100%;

  :deep(.panel-content.right) {
    max-width: 683px;
    width: 100%;
  }

  .navbar-container {
    border-bottom: none;
  }

  &__content {
    max-width: 571px;
    width: 100%;
    margin: 0 auto;
    padding: @size-spacing-x800 @size-spacing-x500 @size-spacing-x1400;
  }

  &__heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 492px;
    width: 100%;
    margin-bottom: @size-spacing-x500;
  }

  &__header {
    margin-bottom: @size-spacing-x800;
  }

  &__details {
    background-color: @background-tertiary;
    padding-bottom: @size-spacing-x500;
    border: none;
  }

  :deep(.row-item) {
    background-color: transparent;
  }

  :deep(.row-item > div) {
    padding: @size-spacing-x450 0;
  }

  &__button-container {
    position: fixed;
    bottom: 0;
    right: 0;
    max-width: 683px;
    width: 100%;
    background: @background-secondary;
    z-index: 10;
  }
}
</style>
